// AutoBIM Logic - Web API client (mirrors mobile src/lib/api.ts).
// Uses REACT_APP_BACKEND_URL + localStorage for token & device hash.
const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "app:token";
const DEVICE_HASH_KEY = "app:device_hash";

let _cachedDeviceHash = null;

// Current UI language (persisted by src/lib/i18n saveLang). Used to localise
// server-generated PDF exports so they match the app language.
function _currentLang() {
  try {
    return localStorage.getItem("autobimlogic:language") || "ar";
  } catch {
    return "ar";
  }
}

function getOrCreateDeviceHash() {
  if (_cachedDeviceHash) return _cachedDeviceHash;
  let h = "";
  try {
    h = localStorage.getItem(DEVICE_HASH_KEY) || "";
  } catch { /* ignore */ }
  if (!h) {
    h =
      "dev-" +
      Math.random().toString(36).slice(2) +
      Date.now().toString(36) +
      Math.random().toString(36).slice(2);
    try {
      localStorage.setItem(DEVICE_HASH_KEY, h);
    } catch { /* ignore */ }
  }
  _cachedDeviceHash = h;
  return h;
}

function currentDeviceOS() {
  try {
    if (typeof navigator !== "undefined") return navigator.userAgent.slice(0, 80);
  } catch { /* ignore */ }
  return "web";
}

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}
export function setToken(token) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch { /* ignore */ }
}
export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore */ }
}

async function request(path, init = {}) {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    "X-Device-Hash": getOrCreateDeviceHash(),
    "X-Device-Platform": "web",
    "X-Device-OS": currentDeviceOS(),
    ...(init.headers || {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}/api${path}`, { ...init, headers });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const j = await res.json();
      detail = j.detail || JSON.stringify(j);
    } catch { /* ignore */ }
    throw new Error(detail);
  }
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  return res.text();
}

// Trigger a browser download for a blob-returning export endpoint.
async function downloadBlob(path, body, filename, method = "POST") {
  const token = getToken();
  const res = await fetch(`${BASE}/api${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { filename, size: blob.size };
}

export const api = {
  // ── Auth ──
  register: (email, password, full_name) =>
    request("/auth/register", { method: "POST", body: JSON.stringify({ email, password, full_name }) }),
  login: (email, password) =>
    request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  guestSignup: () => request("/auth/guest", { method: "POST" }),
  me: () => request("/auth/me"),
  updateSettings: (data) => request("/auth/settings", { method: "PATCH", body: JSON.stringify(data) }),
  changePassword: (current_password, new_password) =>
    request("/auth/change-password", { method: "POST", body: JSON.stringify({ current_password, new_password }) }),
  deleteAccount: (password) =>
    request("/auth/me", { method: "DELETE", body: JSON.stringify({ password, confirm: "confirm" }) }),
  effectiveTier: () => request("/auth/effective-tier"),
  myDevices: () => request("/auth/devices"),
  revokeMyDevice: (device_id) => request(`/auth/devices/${device_id}`, { method: "DELETE" }),

  // ── Contact ──
  listContactMethods: () => request("/contact/methods"),
  sendContactMessage: (subject, body, contact_back) =>
    request("/contact/messages", { method: "POST", body: JSON.stringify({ subject, body, contact_back }) }),

  // ── Banners ──
  listBanners: () => request("/ads/banners"),
  listPublicBanners: () => request("/ads/public-banners"),
  trackBannerClick: (id) => request(`/ads/banners/${id}/track`, { method: "POST" }),
  // ── Ads network (AdSense / AdMob) config ──
  getAdsConfig: () => request("/ads/config"),
  adminUpdateAdsConfig: (body) => request("/admin/ads/config", { method: "PUT", body }),

  // ── Billing (public) ──
  getPricing: () => request("/pricing"),
  getMySubscription: () => request("/me/subscription"),
  redeemPromo: (code) => request("/promo/redeem", { method: "POST", body: JSON.stringify({ code }) }),
  listPaymentMethods: () => request("/payment-methods"),
  submitActivationRequest: (data) => request("/activation-requests", { method: "POST", body: JSON.stringify(data) }),
  myActivationRequests: () => request("/me/activation-requests"),

  // ── Firms ──
  createFirm: (body) => request("/firms", { method: "POST", body: JSON.stringify(body) }),
  myFirm: () => request("/firms/me"),
  inviteMember: (email) => request("/firms/me/invite", { method: "POST", body: JSON.stringify({ email }) }),
  revokeInvitation: (inv_id) => request(`/firms/me/invite/${inv_id}`, { method: "DELETE" }),
  acceptFirmInvite: (token) => request("/firms/accept", { method: "POST", body: JSON.stringify({ token }) }),
  removeFirmMember: (user_id) => request(`/firms/me/members/${user_id}`, { method: "DELETE" }),

  // ── Code Profiles ──
  listCodeProfiles: () => request("/code-profiles"),
  getCodeProfile: (code_id) => request(`/code-profiles/${encodeURIComponent(code_id)}`),
  codeSystems: (code_id) => request(`/code-profiles/${encodeURIComponent(code_id)}/systems`),
  crossDisciplineQA: (body) => request("/qa/cross-discipline", { method: "POST", body: JSON.stringify(body) }),

  // ── Editor engine ──
  editorValidate: (model) => request("/editor/validate", { method: "POST", body: JSON.stringify(model) }),
  editorAnalyzeLayout: (model) => request("/editor/analyze-layout", { method: "POST", body: JSON.stringify(model) }),
  editorSnap: (model, tolerance_m = 0.1) =>
    request("/editor/snap", { method: "POST", body: JSON.stringify({ model, tolerance_m }) }),
  editorReport: (model) => request("/editor/report", { method: "POST", body: JSON.stringify(model) }),
  editorOptimize: (model) => request("/editor/optimize", { method: "POST", body: JSON.stringify(model) }),
  editorOptimizeApply: (model) => request("/editor/optimize/apply", { method: "POST", body: JSON.stringify(model) }),
  editorMaterials: (data) => request("/editor/materials", { method: "POST", body: JSON.stringify(data) }),
  editorStructuralDetails: (data) => request("/editor/structural/details", { method: "POST", body: JSON.stringify(data) }),
  editorStructuralSchedule: (data) => request("/editor/structural/schedule", { method: "POST", body: JSON.stringify(data) }),
  editorSmartFoundation: (data) => request("/editor/structural/smart-foundation", { method: "POST", body: JSON.stringify(data) }),
  editorCodeCheck: (data) => request("/editor/code-check", { method: "POST", body: JSON.stringify(data) }),
  editorTakeoff: (data) => request("/editor/takeoff", { method: "POST", body: JSON.stringify(data) }),
  editorSection: (data) => request("/editor/section", { method: "POST", body: JSON.stringify(data) }),
  editorCirculation: (data) => request("/editor/circulation", { method: "POST", body: JSON.stringify(data) }),
  editorFloorPlans: (data) => request("/editor/floor-plans", { method: "POST", body: JSON.stringify(data) }),
  editorSchedules: (data) => request("/editor/schedules", { method: "POST", body: JSON.stringify(data) }),
  editorMEP: (data) => request("/editor/mep", { method: "POST", body: JSON.stringify(data) }),
  editorMepBoq: (data) =>
    downloadBlob("/editor/mep/export/boq", data,
      `MEP-BOQ-${(data.project_name || "Plan").replace(/[^a-z0-9_ -]/gi, "")}.pdf`),
  mepReport: (model, floors = 1) => request("/editor/mep/report", { method: "POST", body: JSON.stringify({ model, floors }) }),
  editorShadow: (data) => request("/editor/shadow", { method: "POST", body: JSON.stringify(data) }),
  editorShadowCities: () => request("/editor/shadow/cities"),
  editorListDesignCodes: () => request("/editor/codes/design"),
  editorListSlabSystems: () => request("/editor/codes/slab-systems"),
  editorExplainPlan: (model) => request("/editor/explain", { method: "POST", body: JSON.stringify(model) }),
  editorSuggestFixes: (model) => request("/editor/suggest-fixes", { method: "POST", body: JSON.stringify(model) }),
  editorDefaultRates: () => request("/editor/codes/default-rates"),

  // Editor projects (persisted plans)
  editorCreateProject: (data) => request("/editor/projects", { method: "POST", body: JSON.stringify(data) }),
  editorUpdateProject: (id, data) => request(`/editor/projects/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  editorGetProject: (id) => request(`/editor/projects/${id}`),
  editorListProjects: () => request("/editor/projects"),
  editorDeleteProject: (id) => request(`/editor/projects/${id}`, { method: "DELETE" }),

  // Wizard
  wizardListBlocks: () => request("/wizard/blocks"),
  wizardListTemplates: (kind) => request(`/wizard/templates${kind ? `?kind=${kind}` : ""}`),
  wizardRecommend: (data) => request("/wizard/recommend", { method: "POST", body: JSON.stringify(data) }),
  wizardInstantiateTemplate: (template_id) => request(`/wizard/templates/${template_id}/instantiate`, { method: "POST" }),

  // Generators
  editorGenerateVariants: (data) => request("/editor/generate/variants", { method: "POST", body: JSON.stringify(data) }),
  generateVilla: (data) => request("/editor/generate/villa", { method: "POST", body: JSON.stringify(data) }),
  generateApartment: (data) => request("/editor/generate/apartment", { method: "POST", body: JSON.stringify(data) }),
  editorGenerate: (kind, data) => request(`/editor/generate/${kind}`, { method: "POST", body: JSON.stringify(data) }),

  // Exports (blob downloads)
  editorDownloadExport: (format, model, projectName, meta) => {
    const extMap = { dxf: "dxf", pdf: "pdf", boq: "xlsx", "pdf-pack": "pdf", "hordi-dxf": "dxf", "delivery-zip": "zip" };
    const suffixMap = { boq: "_BOQ", "pdf-pack": "_PACK", "hordi-dxf": "_HORDI", "delivery-zip": "_DELIVERY" };
    const ext = extMap[format] || format;
    const suffix = suffixMap[format] || "";
    const filename = `${(projectName || "Plan").replace(/[^a-z0-9_ -]/gi, "")}${suffix}.${ext}`;
    return downloadBlob(`/editor/export/${format}`, { model, project_name: projectName, lang: _currentLang(), ...(meta || {}) }, filename);
  },
  editorDownloadSheet: (sheet, model, projectName, meta) => {
    const safe = (projectName || "Plan").replace(/[^a-z0-9_ -]/gi, "");
    const filename = `${safe}_${sheet.toUpperCase()}.pdf`;
    return downloadBlob("/editor/export/sheet", { model, sheet, project_name: projectName, lang: _currentLang(), ...(meta || {}) }, filename);
  },
  editorListSheets: () => request("/editor/sheets"),
  editorExportIFC: (model, projectName, floors = 1, wallHeight = 3.0) =>
    downloadBlob(
      "/editor/export/ifc",
      { model, project_name: projectName, floors, wall_height_m: wallHeight },
      `${(projectName || "Plan").replace(/[^a-z0-9_ -]/gi, "")}.ifc`
    ),
  editorTakeoffExport: (fmt, data) =>
    downloadBlob(
      `/editor/takeoff/export/${fmt}`,
      data,
      `BoQ-${(data.project_name || "plan").replace(/[^a-z0-9_ -]/gi, "")}.${fmt}`
    ),
  editorDxfExport: (data) =>
    downloadBlob("/editor/export/dxf", data, `${(data.project_name || "plan").replace(/[^a-z0-9_ -]/gi, "")}.dxf`),
  editorElementDetailPdf: (payload) =>
    downloadBlob(
      "/editor/element-detail-pdf",
      payload,
      `detail_${(payload.title || "element").replace(/[^a-z0-9_-]/gi, "")}.pdf`
    ),
  editorExportPlanExplanation: (model, projectName) =>
    downloadBlob(
      "/editor/export/plan-explanation",
      { model, project_name: projectName, lang: _currentLang(), share_base_url: (typeof window !== "undefined" ? window.location.origin : "") },
      `${(projectName || "Plan").replace(/[^a-z0-9_ -]/gi, "")}_explanation.pdf`
    ),

  // ── Import (DXF / PDF) + deterministic auto-completion ──
  editorImportFile: async (file, kind /* "dxf" | "pdf" */, autoComplete = true) => {
    const token = getToken();
    const form = new FormData();
    form.append("file", file);
    form.append("auto_complete", autoComplete ? "true" : "false");
    const headers = {
      "X-Device-Hash": getOrCreateDeviceHash(),
      "X-Device-Platform": "web",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${BASE}/api/editor/import/${kind}`, {
      method: "POST", headers, body: form,
    });
    if (!res.ok) {
      let detail = `${res.status}`;
      try { const j = await res.json(); detail = j.detail || JSON.stringify(j); } catch { /* ignore */ }
      throw new Error(detail);
    }
    return res.json();
  },

  // Templates (project-scope)
  listTemplateCategories: () => request("/templates/categories"),
  listTemplates: (category) => request(`/templates${category ? `?category=${category}` : ""}`),
  matchTemplates: (data) => request("/templates/match", { method: "POST", body: JSON.stringify(data) }),

  // ── Admin ──
  adminStats: () => request("/admin/stats"),
  adminListUsers: (q = "", skip = 0, limit = 50) =>
    request(`/admin/users?q=${encodeURIComponent(q)}&skip=${skip}&limit=${limit}`),
  adminGetUser: (uid) => request(`/admin/users/${uid}`),
  adminUpdateUser: (uid, data) => request(`/admin/users/${uid}`, { method: "PATCH", body: JSON.stringify(data) }),
  adminDeleteUser: (uid) => request(`/admin/users/${uid}`, { method: "DELETE" }),
  adminSubscriptionAction: (uid, action, duration_days = 30, tier = "pro") =>
    request(`/admin/users/${uid}/subscription`, { method: "POST", body: JSON.stringify({ action, tier, duration_days }) }),
  adminGetPricing: () => request("/admin/pricing"),
  adminUpdatePricing: (payload) => request("/admin/pricing", { method: "PUT", body: JSON.stringify(payload) }),
  adminMessageUser: (uid, title, body) => request(`/admin/users/${uid}/message`, { method: "POST", body: JSON.stringify({ title, body }) }),
  adminBroadcast: (title, body, audience = "all") => request("/admin/broadcast", { method: "POST", body: JSON.stringify({ title, body, audience }) }),
  myMessages: () => request("/auth/messages"),
  markMessageRead: (id) => request(`/auth/messages/${id}/read`, { method: "POST" }),
  adminListPromos: () => request("/admin/promos"),
  adminCreatePromo: (data) => request("/admin/promos", { method: "POST", body: JSON.stringify(data) }),
  adminTogglePromo: (code, active) => request(`/admin/promos/${code}`, { method: "PATCH", body: JSON.stringify({ active }) }),
  adminDeletePromo: (code) => request(`/admin/promos/${code}`, { method: "DELETE" }),
  adminSetFreeMode: (enabled) => request("/admin/free-mode", { method: "POST", body: JSON.stringify({ enabled }) }),
  adminSetMaxDevices: (max_devices) => request("/admin/max-devices", { method: "POST", body: JSON.stringify({ max_devices }) }),
  adminListUserDevices: (uid) => request(`/admin/users/${uid}/devices`),
  adminRevokeUserDevice: (uid, device_id) => request(`/admin/users/${uid}/devices/${device_id}`, { method: "DELETE" }),
  adminListFirms: (q = "", skip = 0, limit = 50) =>
    request(`/admin/firms?q=${encodeURIComponent(q)}&skip=${skip}&limit=${limit}`),
  adminFirmStats: () => request("/admin/firms/stats"),
  adminFirmDetail: (fid) => request(`/admin/firms/${fid}`),
  adminSetFirmSeats: (fid, max_seats) => request(`/admin/firms/${fid}/seats`, { method: "POST", body: JSON.stringify({ max_seats }) }),
  adminDeleteFirm: (fid) => request(`/admin/firms/${fid}`, { method: "DELETE" }),
  adminListPaymentMethods: () => request("/admin/payment-methods"),
  adminCreatePaymentMethod: (data) => request("/admin/payment-methods", { method: "POST", body: JSON.stringify(data) }),
  adminUpdatePaymentMethod: (id, data) => request(`/admin/payment-methods/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  adminDeletePaymentMethod: (id) => request(`/admin/payment-methods/${id}`, { method: "DELETE" }),
  adminListActivationRequests: (status, skip = 0, limit = 50) =>
    request(`/admin/activation-requests?skip=${skip}&limit=${limit}${status ? `&status=${status}` : ""}`),
  adminApproveActivation: (id, notes = "") =>
    request(`/admin/activation-requests/${id}/approve`, { method: "POST", body: JSON.stringify({ notes }) }),
  adminRejectActivation: (id, reason = "") =>
    request(`/admin/activation-requests/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) }),
  adminListContactMethods: () => request("/admin/contact-methods"),
  adminCreateContactMethod: (m) => request("/admin/contact-methods", { method: "POST", body: JSON.stringify(m) }),
  adminUpdateContactMethod: (id, m) => request(`/admin/contact-methods/${id}`, { method: "PATCH", body: JSON.stringify(m) }),
  adminDeleteContactMethod: (id) => request(`/admin/contact-methods/${id}`, { method: "DELETE" }),
  adminListContactMessages: () => request("/admin/contact-messages"),
  adminUpdateContactMessage: (id, status) => request(`/admin/contact-messages/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  adminListBanners: () => request("/admin/ads/banners"),
  adminCreateBanner: (b) => request("/admin/ads/banners", { method: "POST", body: JSON.stringify(b) }),
  adminUpdateBanner: (id, b) => request(`/admin/ads/banners/${id}`, { method: "PATCH", body: JSON.stringify(b) }),
  adminDeleteBanner: (id) => request(`/admin/ads/banners/${id}`, { method: "DELETE" }),
};
